/**
 * Where the client looks for the two servers.
 *
 * Pure, and separated from the modules that use it, for the reason docs/05 Phase 1
 * records about the camera: this is the kind of thing that is wrong in a way that looks
 * completely fine. A `ws://` default works on every developer machine there has ever
 * been, and fails on every HTTPS page — the browser refuses to open a plaintext socket
 * from a secure origin, so the failure appears for the first time in production, on a
 * build that passed every check.
 *
 * The rule is that **the scheme follows the page**. A page served over TLS reaches its
 * servers over TLS; a page served over plain HTTP does not. There is no third case worth
 * supporting: a secure page talking to an insecure backend is blocked by the browser, and
 * an insecure page talking to a secure backend works but is pointless, since the document
 * it came from was not protected.
 *
 * `VITE_API_URL` and `VITE_MATCH_SERVER` override both, and a real deployment should set
 * them — the servers rarely live on the same host as the static bundle. These defaults
 * exist so that development, a LAN playtest and a single-host deployment all work with no
 * configuration at all.
 */

export interface PageLocation {
  protocol: string;
  hostname: string;
}

/** Where the API lives when nothing has been configured. */
export const DEFAULT_API_PORT = 8788;
/** Where the match server lives when nothing has been configured. */
export const DEFAULT_MATCH_PORT = 8787;

/**
 * @param secure whether the page itself was served over TLS
 */
export function httpOrigin(location: PageLocation, port: number): string {
  return `${location.protocol === 'https:' ? 'https' : 'http'}://${location.hostname}:${port}`;
}

export function wsOrigin(location: PageLocation, port: number): string {
  return `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.hostname}:${port}`;
}

/**
 * Resolve a configured override against a default.
 *
 * An override that is present but empty is treated as absent. That is not pedantry: a
 * build pipeline that sets `VITE_API_URL=` — an unset variable interpolated into a shell
 * command, which is the single most common way this goes wrong — would otherwise produce
 * a client that fetches from the empty string and fails every call with no clue why.
 */
export function resolveEndpoint(configured: string | undefined, fallback: string): string {
  const trimmed = configured?.trim();
  return trimmed ? trimmed.replace(/\/+$/, '') : fallback;
}
